<script setup lang="ts">
import { Link } from '@inertiajs/vue3';
import { computed } from 'vue';
import { useI18n } from '@/composables/useI18n';
import {
    faq,
    privacy,
    terms,
    antiSpam,
    antiBanGuide,
    status,
    contact,
    pricing,
    security,
} from '@/routes/public';
import { index as apiIndex } from '@/routes/public/api';
import {
    index as productIndex,
    instances,
    validation,
    messages,
    templates,
    campaigns,
} from '@/routes/public/product';
import { blog, faq as resourcesFaq } from '@/routes/public/resources';
import {
    marketing,
    sales,
    notifications,
    integrations,
} from '@/routes/public/solutions';
import { whatsappFormatter } from '@/routes/public/tools';

const { __ } = useI18n();

const footerGroups = computed(() => [
    {
        title: __('footer.product'),
        links: [
            { label: __('footer.overview'), href: productIndex().url },
            { label: __('footer.whatsapp_instances'), href: instances().url },
            { label: __('footer.number_validation'), href: validation().url },
            { label: __('footer.messages'), href: messages().url },
            { label: __('footer.dynamic_templates'), href: templates().url },
            { label: __('footer.campaigns'), href: campaigns().url },
        ],
    },
    {
        title: __('footer.solutions'),
        links: [
            { label: __('footer.marketing_campaigns'), href: marketing().url },
            { label: __('footer.sales_followups'), href: sales().url },
            {
                label: __('footer.transactional_notifications'),
                href: notifications().url,
            },
            {
                label: __('footer.business_integrations'),
                href: integrations().url,
            },
        ],
    },
    {
        title: __('footer.api'),
        links: [
            { label: __('footer.overview'), href: apiIndex().url },
            { label: __('footer.send_message'), href: '/api/envoi-message' },
            {
                label: __('footer.validate_number'),
                href: '/api/validation-numero',
            },
            {
                label: __('footer.authentication'),
                href: '/api/authentification',
            },
            { label: __('footer.examples'), href: '/api/exemples' },
        ],
    },
    {
        title: __('footer.resources'),
        links: [
            { label: __('footer.blog'), href: blog().url },
            { label: __('footer.faq'), href: resourcesFaq().url },
            { label: __('footer.api_docs'), href: apiIndex().url },
            { label: __('footer.pricing'), href: pricing().url },
            {
                label: __('footer.whatsapp_formatter'),
                href: whatsappFormatter().url,
            },
        ],
    },
    {
        title: __('footer.legal'),
        links: [
            { label: __('footer.contact'), href: contact().url },
            { label: __('footer.terms'), href: terms().url },
            { label: __('footer.privacy'), href: privacy().url },
            { label: __('footer.anti_spam'), href: antiSpam().url },
            {
                label: __('footer.prevent_ban'),
                href: antiBanGuide().url,
            },
            { label: __('footer.security'), href: security().url },
            { label: __('footer.platform_status'), href: status().url },
            { label: __('footer.faq'), href: faq().url },
        ],
    },
]);
</script>

<template>
    <footer class="border-t border-border bg-card">
        <div class="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
            <div class="grid gap-8 sm:grid-cols-2 lg:grid-cols-5">
                <div class="lg:col-span-1">
                    <Link
                        href="/"
                        class="flex shrink-0 items-center gap-2 font-semibold text-foreground"
                    >
                        <img
                            src="/logo/light.png"
                            alt="KwikTalk"
                            class="block h-7 w-auto dark:hidden"
                        />
                        <img
                            src="/logo/dark.png"
                            alt="KwikTalk"
                            class="hidden h-7 w-auto dark:block"
                        />
                    </Link>
                    <p class="mt-3 text-sm text-muted-foreground">
                        {{ __('footer.tagline') }}
                    </p>
                </div>
                <div
                    v-for="group in footerGroups"
                    :key="group.title"
                    class="lg:col-span-1"
                >
                    <h3 class="mb-3 text-sm font-semibold text-foreground">
                        {{ group.title }}
                    </h3>
                    <ul class="space-y-2">
                        <li v-for="link in group.links" :key="link.label">
                            <Link
                                :href="link.href"
                                class="text-sm text-muted-foreground transition-colors hover:text-foreground"
                            >
                                {{ link.label }}
                            </Link>
                        </li>
                    </ul>
                </div>
            </div>
            <div
                class="mt-8 border-t border-border pt-8 text-center text-sm text-muted-foreground"
            >
                &copy; {{ new Date().getFullYear() }} KwikTalk.
                {{ __('footer.rights_reserved') }}
            </div>
        </div>
    </footer>
</template>
